/**
 * ROOM UNREAD (MENTIONS.md §5) — Slack-style: the rooms with activity since I last looked (not my own) → the sidebar
 * bolds them; opening/watching a room catches me up. ONE shared cache:
 *  • authoritative load on mount / tab focus / workspace change (GET /auth/rooms/seen);
 *  • LIVE: the per-workspace activity beacon (``stepflow:room-activity``, re-broadcast once by the shell from the
 *    tenant channel) marks that room unread LOCALLY — no request per message, so it scales with the workspace;
 *  • a room I'm in is never marked (I'm watching it) and ``stepflow:rooms-seen-changed`` clears it at once.
 */
import { useEffect, useState } from 'react';
import { getUnreadRooms } from '../services/mentionsApi.js';

export const ROOM_ACTIVITY_EVENT = 'stepflow:room-activity';
export const ROOMS_SEEN_EVENT = 'stepflow:rooms-seen-changed';
const store = { rooms: new Set(), key: '', inflight: null, subs: new Set() };
const emit = () => { const snap = new Set(store.rooms); store.subs.forEach((f) => f(snap)); };
export const roomKey = (networkId, poolId) => `${networkId}:${poolId || 'default'}`;

function currentRoomKey() {
  if (typeof window === 'undefined') return '';
  if (!/\/agentic/.test(window.location.pathname || '')) return '';
  const q = new URLSearchParams(window.location.search || '');
  return q.get('networkId') ? roomKey(q.get('networkId'), q.get('poolId')) : '';
}

function load(key) {
  if (!key) { store.rooms = new Set(); emit(); return Promise.resolve(); }
  if (store.inflight) return store.inflight;
  store.key = key;
  store.inflight = getUnreadRooms()
    .then((d) => { const here = currentRoomKey(); store.rooms = new Set((d.rooms || []).filter((k) => k !== here)); emit(); })
    .catch(() => {})
    .finally(() => { store.inflight = null; });
  return store.inflight;
}

export function __resetRoomUnread() { store.rooms = new Set(); store.key = ''; store.inflight = null; }

/** ``key`` = the active workspace ('' → nothing). → a Set of unread "networkId:poolId". */
export default function useRoomUnread(key) {
  const [rooms, setRooms] = useState(store.rooms);
  useEffect(() => {
    const sub = (s) => setRooms(s);
    store.subs.add(sub);
    load(key);
    const refresh = () => load(key);
    const onActivity = (e) => {
      const d = (e && e.detail) || {};
      if (!d.networkId) return;
      const k = roomKey(d.networkId, d.poolId);
      if (k === currentRoomKey() || store.rooms.has(k)) return;
      store.rooms = new Set(store.rooms).add(k);
      emit();
    };
    const onSeen = (e) => {
      const d = (e && e.detail) || {};
      if (!d.networkId) return;
      const k = roomKey(d.networkId, d.poolId);
      if (!store.rooms.has(k)) return;
      const next = new Set(store.rooms); next.delete(k); store.rooms = next; emit();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener(ROOM_ACTIVITY_EVENT, onActivity);
    window.addEventListener(ROOMS_SEEN_EVENT, onSeen);
    return () => {
      store.subs.delete(sub);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(ROOM_ACTIVITY_EVENT, onActivity);
      window.removeEventListener(ROOMS_SEEN_EVENT, onSeen);
    };
  }, [key]);
  return rooms;
}
